import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { PortMap, PointPort, EdgePort } from '../../../port';
import { computeAngleCenter } from '../../../port';
import type { SolvedRectangle } from '../../../analytics';
import type { PlacedShape } from '../../../placement';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { resolveGeometryStyle } from '../../coloring';
import {
  initShapeCreatorContext,
  HIDDEN_POINT_ATTRS,
  createBorderStyles,
  createRightAngleMarkers,
  applyEdgeVisibilityOverrides,
  QUAD_ANGLE_KEYS,
  QUAD_VERTEX_NAMES,
  resolveBaseVertexStyle,
  buildVertexPerIndex,
} from '../shape-creator.util';
import type { RectangleElement } from './rectangle.model';
import { EDGE_NAMES, PROPERTY_FOR_EDGE } from './rectangle.model';

export function createRectangle(
  element: RectangleElement & { id: string },
  placed: PlacedShape,
  solved: SolvedRectangle,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const ctx = initShapeCreatorContext(element, context);
  const { elementFactory, elementDisplay, board, jsxObjects, labels, styleCtx } = ctx;
  const givenKeys = new Set(Object.keys(element.params));

  const vA = placed.vertices['A'] as [number, number];
  const vB = placed.vertices['B'] as [number, number];
  const _vC = placed.vertices['C'] as [number, number];
  const vD = placed.vertices['D'] as [number, number];

  // --- Create constrained point system ---

  const pA = board.create('point', vA, { ...HIDDEN_POINT_ATTRS, name: 'A' }) as JXGElement;
  const pB = board.create('point', vB, { ...HIDDEN_POINT_ATTRS, name: 'B' }) as JXGElement;

  const baseLine = board.create('line', [pA, pB], {
    visible: false,
    strokeWidth: 0,
    fixed: true,
    highlight: false,
  }) as JXGElement;
  const perpLine = board.create('perpendicular', [baseLine, pA], {
    visible: false,
    strokeWidth: 0,
    fixed: true,
    highlight: false,
  }) as JXGElement;
  const pD = board.create('glider', [vD[0], vD[1], perpLine], {
    ...HIDDEN_POINT_ATTRS,
    name: 'D',
  }) as JXGElement;

  const tA = pA as unknown as { X: () => number; Y: () => number };
  const tB = pB as unknown as { X: () => number; Y: () => number };
  const tD = pD as unknown as { X: () => number; Y: () => number };
  const pC = board.create('point', [() => tB.X() + (tD.X() - tA.X()), () => tB.Y() + (tD.Y() - tA.Y())], {
    ...HIDDEN_POINT_ATTRS,
    name: 'C',
  }) as JXGElement;

  jsxObjects.push(baseLine, perpLine);

  // --- Build border styles using resolved cascade ---
  const borderPerEdge = createBorderStyles(EDGE_NAMES, PROPERTY_FOR_EDGE, 'Rectangle', element, givenKeys, ctx);

  // --- Create polygon ---
  const polyStyle = resolveGeometryStyle('polygon', 'Rectangle', styleCtx, elementDisplay);
  const borderStyle = resolveGeometryStyle('line', 'Rectangle', styleCtx, elementDisplay);
  const polygon = elementFactory.polygon({
    vertices: [pA, pB, pC, pD],
    name: element.id,
    vertexNames: ['A', 'B', 'C', 'D'],
    style: {
      fillColor: polyStyle.fillColor,
      fillOpacity: polyStyle.fillOpacity,
      strokeWidth: 0,
      vertex: resolveBaseVertexStyle('Rectangle', ctx),
      vertexPerIndex: buildVertexPerIndex(QUAD_VERTEX_NAMES, 'Rectangle', ctx),
      border: { strokeWidth: borderStyle.strokeWidth ?? 2, color: borderStyle.color },
      borderPerEdge,
    },
  });

  jsxObjects.push(polygon.polygon, ...polygon.vertices, ...polygon.borders);
  jsxObjects.push(...polygon.helpers);
  labels.push(...polygon.borderLabels);

  // --- Create right-angle markers (always created, hidden by default) ---
  const angleElements = createRightAngleMarkers(polygon.vertices, 'Rectangle', ctx);

  // --- Apply per-edge visibility overrides ---
  applyEdgeVisibilityOverrides(polygon.borders, EDGE_NAMES, elementDisplay);

  const tC = pC as unknown as { X: () => number; Y: () => number };
  const ports = buildRectanglePorts(tA, tB, tC, tD);

  const namedObjects: Record<string, import('jsxgraph').GeometryElement> = {};
  for (let i = 0; i < EDGE_NAMES.length; i++) {
    namedObjects[`edge.${EDGE_NAMES[i]}`] = polygon.borders[i];
  }
  for (let i = 0; i < QUAD_ANGLE_KEYS.length; i++) {
    namedObjects[`angle.${QUAD_ANGLE_KEYS[i]}`] = angleElements[i];
  }
  namedObjects['point.A'] = polygon.vertices[0];
  namedObjects['point.B'] = polygon.vertices[1];
  namedObjects['point.C'] = polygon.vertices[2];
  namedObjects['point.D'] = polygon.vertices[3];

  return {
    jsxObjects,
    labels,
    ports,
    namedObjects,
    controlPoints: [pA, pB],
    destroy: () => {
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

function buildRectanglePorts(
  a: { X: () => number; Y: () => number },
  b: { X: () => number; Y: () => number },
  c: { X: () => number; Y: () => number },
  d: { X: () => number; Y: () => number },
): PortMap {
  return {
    'point.A': () => [a.X(), a.Y()] as PointPort,
    'point.B': () => [b.X(), b.Y()] as PointPort,
    'point.C': () => [c.X(), c.Y()] as PointPort,
    'point.D': () => [d.X(), d.Y()] as PointPort,

    'edge.bottom': () => ({ start: [a.X(), a.Y()], end: [b.X(), b.Y()] }) as EdgePort,
    'edge.right': () => ({ start: [b.X(), b.Y()], end: [c.X(), c.Y()] }) as EdgePort,
    'edge.top': () => ({ start: [c.X(), c.Y()], end: [d.X(), d.Y()] }) as EdgePort,
    'edge.left': () => ({ start: [d.X(), d.Y()], end: [a.X(), a.Y()] }) as EdgePort,

    'angleCenter.A': () => computeAngleCenter(a, d, b),
    'angleCenter.B': () => computeAngleCenter(b, a, c),
    'angleCenter.C': () => computeAngleCenter(c, b, d),
    'angleCenter.D': () => computeAngleCenter(d, c, a),

    w: () => dist(a, b),
    h: () => dist(b, c),
    d: () => dist(a, c),
    area: () => dist(a, b) * dist(b, c),
    perimeter: () => 2 * (dist(a, b) + dist(b, c)),
  };
}

function dist(a: { X: () => number; Y: () => number }, b: { X: () => number; Y: () => number }): number {
  const dx = b.X() - a.X();
  const dy = b.Y() - a.Y();
  return Math.sqrt(dx * dx + dy * dy);
}
